import { difference } from 'lodash-es';

/* eslint-disable id-length */
export enum CandidatePriority {
  A = 'A',
  /**
   * Only used for job title priority, candidate is split to B1 and B2.
   */
  B = 'B',
  /**
   * B priority candidates who do not have hospital selected.
   */
  B1 = 'B1',
  /**
   * B priority candidates who have hospital selected.
   * Fictive job titles with B2 priority (Assistants for Operation (OTA), Anesthetists (ATA), and Surgeons (CTA)).
   */
  B2 = 'B2',
  /**
   * Candidates with any medical staff type of work excluding dental.
   */
  B3 = 'B3',
  /**
   * Candidates with dental staff (ZFA).
   */
  B4 = 'B4',
  /**
   * Physiotherapeutic job titles.
   */
  B5 = 'B5',
  /**
   * Therapeutic job titles other than Physio.
   */
  B6 = 'B6',
  /**
   * Only used for job title priority, candidate is split to C1-3.
   */
  C = 'C',
  /**
   * With 43b or 53c qualification or yes/in progress.
   */
  C1 = 'C1',
  /**
   * With 45b qualification.
   */
  C2 = 'C2',
  /**
   * No qualification.
   */
  C3 = 'C3',
  /**
   * Has been split to multiple D priorities.
   */
  D = 'D',
  D1 = 'D1',
  D2 = 'D2',
  D3 = 'D3',
  E = 'E',
  E1 = 'E1',
  E2 = 'E2',
  E3 = 'E3',
  F = 'F',
  F1 = 'F1',
  F2 = 'F2',
}
/* eslint-enable id-length */

export const CANDIDATE_PRIORITY_I18N = {
  [CandidatePriority.A]: 'A',
  [CandidatePriority.B]: 'B',
  [CandidatePriority.B1]: 'B1',
  [CandidatePriority.B2]: 'B2',
  [CandidatePriority.B3]: 'B3',
  [CandidatePriority.B4]: 'B4',
  [CandidatePriority.B5]: 'B5',
  [CandidatePriority.B6]: 'B6',
  [CandidatePriority.C]: 'C',
  [CandidatePriority.C1]: 'C1',
  [CandidatePriority.C2]: 'C2',
  [CandidatePriority.C3]: 'C3',
  [CandidatePriority.D]: 'D',
  [CandidatePriority.D1]: 'D1',
  [CandidatePriority.D2]: 'D2',
  [CandidatePriority.D3]: 'D3',
  [CandidatePriority.E]: 'E',
  [CandidatePriority.E1]: 'E1',
  [CandidatePriority.E2]: 'E2',
  [CandidatePriority.E3]: 'E3',
  [CandidatePriority.F]: 'F',
  [CandidatePriority.F1]: 'F1',
  [CandidatePriority.F2]: 'F2',
};

export const HIGH_JOB_PRIORITIES = [
  CandidatePriority.A,
  CandidatePriority.B,
  CandidatePriority.B2,
  CandidatePriority.B3,
  CandidatePriority.B4,
  CandidatePriority.B5,
  CandidatePriority.B6,
];
export const LOW_JOB_PRIORITIES = [CandidatePriority.C, CandidatePriority.D2, CandidatePriority.E, CandidatePriority.F];
export const JOB_PRIORITIES = HIGH_JOB_PRIORITIES.concat(LOW_JOB_PRIORITIES);
export const HIGH_PRIORITIES = [
  CandidatePriority.A,
  CandidatePriority.B1,
  CandidatePriority.B2,
  CandidatePriority.B3,
  CandidatePriority.B4,
  CandidatePriority.B5,
  CandidatePriority.B6,
];
export const LOW_PRIORITIES = [
  CandidatePriority.C1,
  CandidatePriority.C2,
  CandidatePriority.C3,
  CandidatePriority.D1,
  CandidatePriority.D2,
  CandidatePriority.D3,
  CandidatePriority.E1,
  CandidatePriority.E2,
  CandidatePriority.E3,
  CandidatePriority.F1,
  CandidatePriority.F2,
];
export const NON_DOCIA_ACTIVE_PRIORITIES = HIGH_PRIORITIES.concat(LOW_PRIORITIES);
/* eslint-disable id-length */
export type ActivePriority = Exclude<
  CandidatePriority,
  CandidatePriority.B | CandidatePriority.C | CandidatePriority.D | CandidatePriority.E | CandidatePriority.F
>;
/* eslint-enable id-length */

export const DOCIA_ACTIVE_PRIORITIES = [
  CandidatePriority.A,
  CandidatePriority.B,
  CandidatePriority.C,
  CandidatePriority.D,
  CandidatePriority.E,
  CandidatePriority.F,
];

const ALL_ACTIVE_PRIORITIES_SET = new Set<CandidatePriority>([...NON_DOCIA_ACTIVE_PRIORITIES, ...DOCIA_ACTIVE_PRIORITIES]);
export const ALL_ACTIVE_PRIORITIES = Object.values(CandidatePriority).filter(priority => ALL_ACTIVE_PRIORITIES_SET.has(priority));

export const AUTOMATIC_INVITES_PRIORITIES = [
  CandidatePriority.A,
  CandidatePriority.B1,
  CandidatePriority.B2,
  CandidatePriority.B3,
  CandidatePriority.B4,
  CandidatePriority.B5,
  CandidatePriority.B6,
  CandidatePriority.C,
  CandidatePriority.C1,
  CandidatePriority.C2,
  CandidatePriority.C3,
  CandidatePriority.D1,
  CandidatePriority.D2,
  CandidatePriority.D3,
  CandidatePriority.E1,
  CandidatePriority.F1,
];

export const NON_AUTOMATIC_INVITES_PRIORITIES = difference(Object.values(CandidatePriority), AUTOMATIC_INVITES_PRIORITIES);

export const B_PRIORITIES = [
  CandidatePriority.B,
  CandidatePriority.B1,
  CandidatePriority.B2,
  CandidatePriority.B3,
  CandidatePriority.B4,
  CandidatePriority.B5,
  CandidatePriority.B6,
];
export const C_PRIORITIES = [CandidatePriority.C, CandidatePriority.C1, CandidatePriority.C2, CandidatePriority.C3];
export const D_PRIORITIES = [CandidatePriority.D, CandidatePriority.D1, CandidatePriority.D2, CandidatePriority.D3];
export const E_PRIORITIES = [CandidatePriority.E, CandidatePriority.E1, CandidatePriority.E2, CandidatePriority.E3];
export const F_PRIORITIES = [CandidatePriority.F, CandidatePriority.F1, CandidatePriority.F2];

export const RETRIGGER_AUTOMATIC_INVITE_PRIORITIES = [CandidatePriority.A, ...B_PRIORITIES, ...C_PRIORITIES, ...D_PRIORITIES];

export const PRIORITIES_TO_RECEIVE_SMS = [
  CandidatePriority.A,
  CandidatePriority.B1,
  CandidatePriority.B2,
  CandidatePriority.B3,
  CandidatePriority.B4,
  CandidatePriority.B5,
  CandidatePriority.B6,
  CandidatePriority.D1,
  CandidatePriority.D2,
  CandidatePriority.D3,
  CandidatePriority.C1,
  CandidatePriority.C2,
  CandidatePriority.C3,
];

export const CONTACT_JOB_DETECTIVE_OPTION_PRIORITIES = [
  CandidatePriority.A,
  CandidatePriority.B,
  CandidatePriority.B1,
  CandidatePriority.B2,
  CandidatePriority.B3,
  CandidatePriority.B4,
  CandidatePriority.B5,
  CandidatePriority.B6,
  CandidatePriority.C1,
  CandidatePriority.C2,
  CandidatePriority.C3,
  CandidatePriority.D1,
  CandidatePriority.D2,
  CandidatePriority.D3,
];

export const PUSH_PLAN_PRIORITIES_CONFIGURATIONS = [
  CandidatePriority.A,
  CandidatePriority.B, // logically treated as B1/B2
  CandidatePriority.B2,
  CandidatePriority.B3,
  CandidatePriority.B4,
  CandidatePriority.B5,
  CandidatePriority.B6,
  CandidatePriority.C,
  CandidatePriority.D1,
  CandidatePriority.D2, // logically treated as D2/D3
  CandidatePriority.E,
  CandidatePriority.F,
];

export const PUSH_PLAN_PRIORITIES = PUSH_PLAN_PRIORITIES_CONFIGURATIONS.filter(priority => priority !== CandidatePriority.D1);

export const PARENT_PRIORITIES = [
  CandidatePriority.A,
  CandidatePriority.B,
  CandidatePriority.C,
  CandidatePriority.D,
  CandidatePriority.E,
  CandidatePriority.F,
];

export const REACTIVATION_TARGET_PRIORITIES = [...HIGH_PRIORITIES, CandidatePriority.D1];

export const INITIATIVE_APPLICATION_PRIORITIES = [
  CandidatePriority.B1,
  CandidatePriority.B2,
  CandidatePriority.B3,
  CandidatePriority.B4,
  CandidatePriority.B5,
  CandidatePriority.B6,
  CandidatePriority.D1,
];

export const INITIATIVE_INDEXED_PRIORITIES = [
  CandidatePriority.A,
  CandidatePriority.B,
  CandidatePriority.B1,
  CandidatePriority.B2,
  CandidatePriority.B3,
  CandidatePriority.B4,
  CandidatePriority.B5,
  CandidatePriority.B6,
  CandidatePriority.C1,
  CandidatePriority.C2,
  CandidatePriority.D1,
];
